<template>
  <CheckoutStep>
    <PhonelineSetupForm
      v-if="offerStatus !== 'error'"
      :access-token
      :is-loading="
        offerStatus === 'pending' || phonelineSetupStatus === 'pending'
      "
      :offer
      :phoneline-setup
    />

    <BouyguesError v-else @try-again="handleTryAgain" />
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, onMounted } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { useBouyguesStore } from '@backmarket/nuxt-layer-checkout/stores/bouyguesStore'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { getBouyguesMobilePlanOffer } from '~/scopes/checkout/utils/cart'
import { HOME } from '~/scopes/home/route-names'

import BouyguesError from '../../components/BouyguesError/BouyguesError.vue'

import PhonelineSetupForm from './components/PhonelineSetupForm/PhonelineSetupForm.vue'

const route = useRoute()

const { setAccessToken } = useBouyguesStore()
const $httpFetch = useHttpFetch()

const props = defineProps<{
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  swapOffer: BuybackOffer | null
}>()

defineOptions({
  inheritAttrs: false,
})

const bouyguesMobilePlanOffer = computed(() =>
  getBouyguesMobilePlanOffer(props.items),
)

const { data: phonelineSetup, status: phonelineSetupStatus } = useAsyncData(
  () => {
    return $httpFetch('/mobile-plan/api/v1/customer-account/phone-line-setup', {
      method: 'GET',
    })
  },
)

const accessToken = computed(() => {
  return route.query?.accessToken && typeof route.query.accessToken === 'string'
    ? route.query.accessToken
    : phonelineSetup.value?.token || null
})

const {
  data: offer,
  status: offerStatus,
  error,
  refresh,
} = useAsyncData(async () => {
  if (!bouyguesMobilePlanOffer.value) {
    return null
  }

  return $httpFetch('/mobile-plan/api/v1/offers/:offerId', {
    pathParams: { offerId: bouyguesMobilePlanOffer.value.id },
  })
})

async function removeOfferFromCart(offerId: string) {
  return $httpFetch('/bm/cart/services', {
    method: 'DELETE',
    body: { offerId },
  })
}

async function handleTryAgain() {
  if (error.value && error.value.statusCode === 404) {
    if (bouyguesMobilePlanOffer.value)
      await removeOfferFromCart(bouyguesMobilePlanOffer.value.id)

    return await navigateTo({ name: HOME })
  }

  return refresh()
}

const { trackFunnel } = useCheckoutTracking()

onMounted(() => {
  if (accessToken.value) {
    setAccessToken(accessToken.value)
  }

  trackFunnel(props.items, props.swapOffer)
})
</script>
